import { usePromotionUserStore } from '~/stores/promotion-user.store'
import { useMaintenanceGamesStore } from '~/stores/maintenance-games.store'

import { modalList } from '~/resources/popup-notification'
import { athenaGames } from '~/resources/athena-games'

import { GAMES_API, IFRAME_GAME_API } from '~configs/api'
import { IFRAME_GAME_URLS, ULR_LOBBY_IFRAME_GAMES } from '~/configs/page-url'
import { GameProvider } from '~/configs/enums/game'
import { PROMOTION_PLAN_TYPE } from '~/constants'

import type { IPlayGame, PlayGameResponse } from '~composables/games/interface'
import { useUserData } from '~/composables/account/use-user-data'
import { useLogin } from '~/composables/account/use-login'
import { useModal } from '~/composables/common/use-modal'
import { useApi } from '~/composables/common/use-api'
import { notificationPopup } from '~/utils/modal/notify'

export function useGames() {
  const { request } = useApi()
  const environment = useRuntimeConfig().public
  const route = useRoute()
  const router = useRouter()
  const { showModalLogin } = useLogin()
  const { isMobile, isSafari } = useDevice()
  const userStore = usePromotionUserStore()
  const { currentUser } = useUserData()
  const {
    LODE,
    LODE_SIEUTOC,
    LODE_3MIEN,
    LODE_DAICAT,
    TP_NUMBER_GAME,
    TP_KENO,
    LOTTERY_LAS_VEGAS,
    LOTTERY_CYBER_PUNK,
    QUAYSO_ATOM,
    KENO_VIETLOT
  } = IFRAME_GAME_URLS
  const { closeAllModal } = useModal()

  async function playGame({
    apiUrl = '',
    gameId = '',
    loginRequired = false,
    newTab = false,
    promotionPrevent = false,
    gameUrl = '',
    gameType = '',
    provider = '',
    isCommingSoon = false,
    withoutIframe = false,
    category = ['']
  }: IPlayGame): Promise<void> {
    if (isCommingSoon) {
      return
    }
    if (isMaintainAthenaGames(gameId) || isMaintainAthenaGames(gameUrl.replaceAll('/', ''))) {
      openModalMaintainAthenaGames()
      return
    }

    if (isMobile) {
      newTab = true
    }

    if (loginRequired && !currentUser?.value.token) {
      showModalLogin()
      return
    }

    let denyList = currentUser ? currentUser.value.deny_game_ids : []
    denyList = denyList?.map((game: string) => game.replace(/_/g, ''))

    if ((provider === GameProvider.PG_SOFT && category?.includes('slots')) || denyList?.includes(gameId)) {
      promotionPrevent = true
    }

    if (visibleRollingChart() && promotionPrevent) {
      closeAllModal()
      if (isMobile) {
        notificationPopup(modalList.GAME_PROMOTION)
        return
      } else {
        notificationPopup(modalList.GAME_PROMOTION_PC)
        return
      }
    }

    const urlOpenIframe = gameUrl ? getGameUrlIframe(gameUrl) : ''

    if ((ULR_LOBBY_IFRAME_GAMES.includes(gameUrl) || gameUrl.includes(IFRAME_GAME_URLS.NUMBER_GAME)) && !isMobile) {
      void router.push(gameUrl)
      return
    }
    if (urlOpenIframe) {
      openGameAbsoluteUrl(urlOpenIframe, gameUrl, newTab)
      return
    }
    if (gameUrl) {
      await openGameRelativeUrl(gameUrl, newTab)
      return
    }
    void openGame(apiUrl, gameId, gameType, provider, newTab, withoutIframe)
  }

  const notifyGameMaintenance = (openedTab: Window | null) => {
    closeAllModal()
    notificationPopup(modalList.GAME_MAINTENANCE)
    openedTab?.close()
  }

  async function openGame(
    apiUrl: string = '',
    gameId: string = '',
    gameType: string = '',
    provider: string = '',
    isNewTab: boolean = false,
    withoutIframe: boolean = false
  ): Promise<void> {
    const siteDomain: string = environment.SITE_DOMAIN
    const returnUrl: string = encodeURIComponent(window.location.href)

    const queryString = apiUrl.split('?')[1]

    const params = new URLSearchParams(queryString)
    params.append('provider', provider)
    params.append('homeUrl', siteDomain)
    params.append('gameId', gameId)
    params.append('gameType', gameType)
    params.append('returnUrl', returnUrl)
    const apiPath = `${apiUrl}?${params.toString()}`

    let tabNew: Window | null = null
    if (isSafari) {
      const target = isNewTab ? '_blank' : '_self'
      tabNew = window.open('', target)
    }

    const data = await getPlayGameUrl(apiPath)
    const content = data?.content ?? ''
    const type = data?.type ?? ''

    if (!content) {
      notifyGameMaintenance(tabNew)
      return
    }

    switch (type) {
      case 'url': {
        let url = content

        if (withoutIframe) {
          url = url.match(/^https?:/) ? url : 'http://' + url
        }

        try {
          if (tabNew) {
            tabNew.location.href = url
          } else if (isNewTab) {
            window.open(url, '_blank')
          }
        } catch {
          notifyGameMaintenance(tabNew)
        }
        break
      }
      case 'html': {
        const html = content

        if (!tabNew) {
          tabNew = window.open('', '_blank')
        }
        tabNew?.document.open()
        tabNew?.document.write(html)
        tabNew?.document.close()

        break
      }
      default: {
        notifyGameMaintenance(tabNew)
        break
      }
    }
  }

  function getGameUrlIframe(gameUrl: string): string {
    const url = gameUrl || route.params?.slug
    switch (url) {
      case LODE:
        return getLodeUrl(isMobile)
      case LODE_DAICAT:
        return getLodeDaiCatUrl()
      case LODE_SIEUTOC:
        return getLodeSieuTocUrl(isMobile)
      case LODE_3MIEN:
        return getLode3mienUrl()
      case TP_KENO:
        return getKenoUrl(isMobile)
      case TP_NUMBER_GAME:
        return getTpNumberGameUrl(isMobile)
      case QUAYSO_ATOM:
        return getAtomLotteryUrl()
      case KENO_VIETLOT:
        return getKenoVietlotUrl()
      case LOTTERY_LAS_VEGAS:
        return getLotteryLasVegasUrl()
      case LOTTERY_CYBER_PUNK:
        return getLotteryCyberPunkUrl(true)
      default:
        return ''
    }
  }

  const getLodeUrl = (isMobile = false): string => {
    const gptoken: string = currentUser?.value.gptoken
    if (isMobile) {
      const loDeMobileUrl: string = environment.LODE_MOBILE_URL
      return `${loDeMobileUrl}${gptoken}`
    }
    const loDeUrl: string = environment.LODE_URL
    return `${loDeUrl}${gptoken}`
  }

  const getLodeDaiCatUrl = (): string => {
    const gptoken = currentUser?.value.gptoken
    const loDeDaiCat: string = environment.LODE_DAICAT_URL
    return `${loDeDaiCat}${gptoken}`
  }

  const getLodeSieuTocUrl = (isMobile = false): string => {
    const gptoken = currentUser?.value.gptoken
    const loDeSieuToc: string = isMobile ? environment.LODE_SIEUTOC_MOBILE_URL : environment.LODE_SIEUTOC_URL
    return `${loDeSieuToc}${gptoken}`
  }

  const getLode3mienUrl = (): string => {
    const gptoken: string = currentUser?.value.gptoken
    const loDeUrl: string = environment.LODE_3MIEN_URL
    return `${loDeUrl}${gptoken}&lang=vi`
  }

  const getKenoUrl = (isMobile = false): string => {
    const gptoken = currentUser?.value.gptoken
      ? `${currentUser.value.gptoken ? `&token=${currentUser.value.gptoken}` : ''}`
      : ''
    const kenoUrl: string = environment.TP_KENO_URL
    const kenoOperatorId: string = environment.KENO_GAME_OPERATOR_ID
    if (isMobile) {
      return `${kenoUrl}?operator_id=${kenoOperatorId}&lang=vi&currency=VND&is_mobile=1${gptoken}`
    }

    return `${kenoUrl}?operator_id=${kenoOperatorId}&lang=vi&currency=VND${gptoken}`
  }

  const getTpNumberGameUrl = (isMobile = false): string => {
    const gptoken = currentUser?.value.gptoken || ''
    const tpNumberGameUrl: string = environment.TP_NUMBERGAME_URL
    const tpNumberGameOperatorId: string = environment.TP_NUMBER_GAME_OPERATOR_ID
    if (isMobile) {
      return `${tpNumberGameUrl}?operator_id=${tpNumberGameOperatorId}
                &lang=vi&currency=VND&is_mobile=1&token=${gptoken}`
    }
    return `${tpNumberGameUrl}?operator_id=${tpNumberGameOperatorId}&lang=vi&currency=VND&token=${gptoken}`
  }

  const getAtomLotteryUrl = (): string => {
    const gptoken = currentUser?.value.gptoken || ''
    const atomUrl: string = environment.QUAYSO_ATOM
    return `${atomUrl}?token=${gptoken}`
  }

  const getKenoVietlotUrl = (): string => {
    const gptoken = currentUser?.value.gptoken || ''
    const kenoVietlotUrl: string = environment.KENO_VIETLOT
    return `${kenoVietlotUrl}${gptoken}`
  }

  const getLotteryLasVegasUrl = (): string => {
    const gptoken = currentUser?.value.gptoken || ''
    const quaySoLasVegas: string = environment.QUAYSO_LAS_VEGAS_URL
    return `${quaySoLasVegas}?token=${gptoken}`
  }

  const getLotteryCyberPunkUrl = (isValidPlan: boolean): string => {
    const gptoken = currentUser?.value.gptoken || ''
    const quaySoCyber: string = isValidPlan ? environment.QUAYSO_CYPER_URL : environment.QUAYSO_LAS_VEGAS_URL
    return `${quaySoCyber}?token=${gptoken}`
  }

  function visibleRollingChart(): boolean {
    if (!userStore.promotionUser.plan) {
      return false
    }
    if (userStore.promotionUser?.plan?.package_id) {
      return [PROMOTION_PLAN_TYPE.WELCOME, PROMOTION_PLAN_TYPE.FIRST_50].includes(
        userStore.promotionUser.plan.package_id
      )
    }
    return false
  }

  function isMaintainAthenaGames(gameId: string = ''): boolean {
    const athennaGameIds = athenaGames.map(game => game.id)
    const { isMaintain } = useMaintenanceGamesStore()
    return isMaintain && athennaGameIds.includes(gameId.toString())
  }

  function openModalMaintainAthenaGames(): void {
    const { startTime, endTime } = useMaintenanceGamesStore()
    const description = `
      <div class="desc mb-1">Thời gian bảo trì từ <strong>${startTime}</strong> đến <strong>${endTime}</strong>
      Vui lòng quay lại sau hoặc chọn game khác.</div>`
    closeAllModal()
    notificationPopup({ ...modalList.GAME_MAINTENANCE, description })
  }

  const openGameAbsoluteUrl = (gameOpenUrl: string, gameUrl: string, isNewTab: boolean): void => {
    if (isNewTab && gameOpenUrl) {
      window.open(gameOpenUrl, '_blank')
    } else {
      window.location.href = gameUrl
    }
  }

  const openGameRelativeUrl = async (gameUrl: string, isNewTab: boolean): Promise<void> => {
    if (gameUrl === '/numbergame?sportId=soccerlottery') {
      const newTab = window.open('', '_blank', 'menubar=1,resizable=1,width=414,height=666')
      const gameUrls = (await getPlayGameUrl(getIframeGameMapGameApi(gameUrl) || gameUrl))?.content

      if (newTab) {
        newTab.location.href = gameUrls ?? ''
      }
      return
    }

    if (!gameUrl.includes('http') && isMobile && isNewTab) {
      const newTab = window.open('', '_blank')

      const url = (await getPlayGameUrl(getIframeGameMapGameApi(gameUrl) || gameUrl))?.content

      if (url) {
        if (newTab) {
          newTab.location.href = url
        }
      } else {
        newTab?.close()
      }
    } else if (isNewTab) {
      window.open(gameUrl, '_blank')
    } else {
      void router.push(gameUrl)
    }
  }

  const getIframeGameMapGameApi = (url = ''): string => {
    const gameUrl = url || (route.params?.slug as string)
    if (gameUrl.includes(IFRAME_GAME_URLS.NUMBER_GAME)) {
      const { sportId, categoryId } = getAllUrlParams(gameUrl)
      if (sportId) {
        return `${IFRAME_GAME_API.NUMBER_GAME}&sportId=${sportId}`
      }
      if (categoryId) {
        return `${IFRAME_GAME_API.NUMBER_GAME}&categoryId=${categoryId}`
      }
    }
    const paramIsMobile: string = isMobile.toString()
    switch (gameUrl.split('?')[0]) {
      case IFRAME_GAME_URLS.A_SPORTS: {
        if (isMobile) {
          const { matchId, leagueId } = getAllUrlParams(gameUrl)
          return `${IFRAME_GAME_API.A_SPORTS}&matchId=${matchId}&leagueId=${leagueId}&isMobile=${paramIsMobile}`
        }
        const mid = route?.query?.mid ?? ''
        return `${IFRAME_GAME_API.A_SPORTS}&matchId=${mid as string}&isMobile=${paramIsMobile}`
      }
      case IFRAME_GAME_URLS.VIRTUAL_SPORTS:
        return `${IFRAME_GAME_API.VIRTUAL_SPORTS}&isMobile=${paramIsMobile}`
      case IFRAME_GAME_URLS.ESPORTS:
        return `${IFRAME_GAME_API.ESPORTS}&isMobile=${paramIsMobile}`
      case IFRAME_GAME_URLS.VIRTUAL_GAME:
        return IFRAME_GAME_API.VIRTUAL_GAME
      case IFRAME_GAME_URLS.SABA_CLUB:
        return IFRAME_GAME_API.SABA_CLUB
      case IFRAME_GAME_URLS.NUMBER_GAME: {
        const { sportId } = route.query
        if (sportId) {
          return `${IFRAME_GAME_API.NUMBER_GAME}&sportId=${sportId as string}`
        }
        return IFRAME_GAME_API.NUMBER_GAME
      }
      case IFRAME_GAME_URLS.KENO:
        return IFRAME_GAME_API.KENO
      case IFRAME_GAME_URLS.LOTTERY:
        return IFRAME_GAME_API.LOTTERY
      case IFRAME_GAME_URLS.TABLE_GAMES:
        return IFRAME_GAME_API.TABLE_GAMES
      case IFRAME_GAME_URLS.VIRTUAL_CASINO:
        return IFRAME_GAME_API.VIRTUAL_CASINO
      case IFRAME_GAME_URLS.M_SPORTS:
        return `${IFRAME_GAME_API.M_SPORTS}&isMobile=${paramIsMobile}`
      case IFRAME_GAME_URLS.C_SPORTS:
        return `${IFRAME_GAME_API.C_SPORTS}&isMobile=${paramIsMobile}`
      case IFRAME_GAME_URLS.K_SPORTS: {
        const { eventId, leagueId } = getAllUrlParams(url)
        return `${IFRAME_GAME_API.K_SPORTS}&isMobile=${paramIsMobile}&eventId=${eventId}&leagueId=${leagueId}`
      }
      case IFRAME_GAME_URLS.V_GAMING:
        return IFRAME_GAME_API.V_GAMING
      default:
        return ''
    }
  }

  const getPlayGameUrl = async (
    apiUrl: string,
    isDeviceMobile: boolean = isMobile
  ): Promise<PlayGameResponse | null> => {
    try {
      const queryString = apiUrl.split('?')[1]

      const params = Object.fromEntries(new URLSearchParams(queryString))
      const { data } = await request(GAMES_API.PLAY, {
        method: 'GET',
        params: { ...params, isMobile: isDeviceMobile }
      })

      return data
    } catch {
      return null
    }
  }

  return {
    playGame,
    getPlayGameUrl,
    getIframeGameMapGameApi,
    getGameUrlIframe,
    openModalMaintainAthenaGames,
    isMaintainAthenaGames
  }
}
